import { sectionRenderer } from '@theme/section-renderer';
import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #searchFallbackStarted = false;

  connectedCallback() {
    this.#runSearchFallback();

    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    super.disconnectedCallback();
  }

  /**
   * When storefront search returns no products, load the same matches predictive search found.
   */
  async #runSearchFallback() {
    const query = this.dataset.searchFallbackQuery?.trim();
    if (!query || this.#searchFallbackStarted) return;

    // Filtered searches that correctly return 0 products must not be replaced
    // by predictive matches (which would drop the active filter params).
    const currentUrl = new URL(window.location.href);
    for (const key of currentUrl.searchParams.keys()) {
      if (key.startsWith('filter.')) {
        this.#clearSearchLoadingState();
        delete this.dataset.searchFallbackQuery;
        return;
      }
    }

    this.#searchFallbackStarted = true;
    this.setAttribute('data-search-loading', '');
    this.classList.add('search-results--loading');

    try {
      const predictiveUrl = new URL(Theme.routes.predictive_search_url, location.origin);
      predictiveUrl.searchParams.set('q', query);
      predictiveUrl.searchParams.set('resources[type]', 'product');
      predictiveUrl.searchParams.set('resources[limit_scope]', 'each');
      predictiveUrl.searchParams.set('options[prefix]', 'last');

      const predictiveHtml = await sectionRenderer.getSectionHTML('predictive-search', false, predictiveUrl);
      const doc = new DOMParser().parseFromString(predictiveHtml, 'text/html');
      const productRows = [...doc.querySelectorAll('.predictive-search-results__card--product[data-product-id]')];
      const titlePhrases = productRows
        .map((row) => {
          const title = row.querySelector('.predictive-search-results__product-row-title')?.textContent?.trim();
          if (!title) return '';
          // First words as a quoted phrase — precise enough for matches, reliable for facets.
          // (id: OR queries often return products with empty search.filters.)
          return title
            .split(/\s+/)
            .slice(0, 5)
            .join(' ')
            .replace(/"/g, '');
        })
        .filter(Boolean);

      const uniquePhrases = [...new Set(titlePhrases)].slice(0, 24);
      const productIds = productRows
        .map((row) => row.getAttribute('data-product-id'))
        .filter((id) => id != null && id !== '');
      const uniqueIds = [...new Set(productIds)].slice(0, 24);

      if (uniquePhrases.length === 0 && uniqueIds.length === 0) {
        this.#showSearchFallbackEmpty();
        return;
      }

      const sectionId = this.getAttribute('section-id');
      if (!sectionId) return;

      const searchUrl = new URL(Theme.routes.search_url, location.origin);
      if (uniquePhrases.length > 0) {
        searchUrl.searchParams.set('q', uniquePhrases.map((phrase) => `"${phrase}"`).join(' OR '));
      } else {
        searchUrl.searchParams.set('q', uniqueIds.map((id) => `id:${id}`).join(' OR '));
      }
      searchUrl.searchParams.set('type', 'product');
      searchUrl.searchParams.set('options[prefix]', 'last');

      await sectionRenderer.renderSection(sectionId, { url: searchUrl, cache: false });

      if (!this.querySelector('[ref="grid"] [ref="cards[]"], [ref="grid"] [data-product-id]')) {
        this.#showSearchFallbackEmpty();
      }
    } catch (error) {
      console.error(error);
      this.#showSearchFallbackEmpty();
    } finally {
      this.#clearSearchLoadingState();
      delete this.dataset.searchFallbackQuery;
    }
  }

  /**
   * Reveal the empty state after the predictive-search fallback finds no products.
   */
  #showSearchFallbackEmpty() {
    this.#clearSearchLoadingState();

    const loading = this.querySelector('[data-search-fallback-loading]');
    const existingEmpty = this.querySelector('[data-search-fallback-empty]');

    if (loading instanceof HTMLElement) {
      loading.hidden = true;
    }

    if (existingEmpty instanceof HTMLElement) {
      existingEmpty.hidden = false;
      return;
    }

    const template = this.querySelector('[data-search-fallback-empty-template]');
    if (!(template instanceof HTMLTemplateElement)) return;

    const empty = template.content.firstElementChild;
    if (!(empty instanceof HTMLElement)) return;

    template.insertAdjacentElement('afterend', empty);
  }

  #clearSearchLoadingState() {
    this.removeAttribute('data-search-loading');
    this.classList.remove('search-results--loading');

    const loading = this.querySelector('[data-search-fallback-loading]');
    if (loading instanceof HTMLElement) {
      loading.hidden = true;
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
